import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { CleanDialog, CleanDialogTitle, CleanDialogContent, CleanDialogActions } from '@/ui';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { AppError, errorMessage } from '@/lib/appErrors';

export default function AccessRequestPrompt() {
  const { user } = useAuth();
  const [queue,setQueue] = useState<(AppError & {moduleId:string})[]>([]);
  const [open,setOpen] = useState(false);
  const [reason,setReason] = useState('');
  const [busy,setBusy] = useState(false);
  const [error,setError] = useState('');
  const [success,setSuccess] = useState('');
  const id = useRef('');
  const seen = useRef(new Map<string,number>());
  const actor = `${user?.tenant_id || ''}:${user?.id || ''}`;
  const current = queue[0];
  useEffect(()=>{
    setQueue([]);setOpen(false);setReason('');setError('');setSuccess('');seen.current.clear();
    const receive=(event:Event)=>{
      const value=(event as CustomEvent<AppError>).detail;
      if(!user || !value?.path?.startsWith('/v1/')) return;
      const key=`${value.permission}:${value.path}:${value.method}`;
      if(Date.now()-(seen.current.get(key)||0)<60000) return;
      seen.current.set(key,Date.now());
      const entry=Object.assign(value,{moduleId:new URLSearchParams(window.location.search).get('module') || ''});
      setQueue(old=>old.some(x=>`${x.permission}:${x.path}:${x.method}`===key)?old:[...old,entry].slice(0,10));
    };
    window.addEventListener('access:denied',receive);
    return ()=>window.removeEventListener('access:denied',receive);
  },[actor]);
  const dismiss=()=>{if(!busy){setOpen(false);setQueue(old=>old.slice(1));setError('');}};
  const send=async()=>{
    if(!current || busy) return;
    setBusy(true);setError('');
    try {
      const result=await api<{duplicate?:boolean}>('/v1/access-requests',{method:'POST',body:{
        id:id.current,permission:current.permission,path:current.path,method:current.method,
        module:current.moduleId,
        reason:reason.trim(),errorRequestId:current.requestId,
      }});
      setSuccess(result.duplicate?'Такой запрос уже ожидает ответа администратора.':'Запрос отправлен. Ответ появится в уведомлениях и разделе «Запросы доступа».');
      setOpen(false);setQueue(old=>old.slice(1));
    }catch(e){setError(errorMessage(e));}finally{setBusy(false);}
  };
  return <>
    <Snackbar open={!!current && !open} anchorOrigin={{vertical:'bottom',horizontal:'center'}}>
      <Alert severity="warning" onClose={dismiss} sx={{maxWidth:650}} action={<Button color="inherit" size="small" onClick={()=>{id.current=crypto.randomUUID();setReason('');setError('');setOpen(true);}}>Запросить доступ</Button>}>
        Недостаточно прав{current?.permissionLabel ? `: ${current.permissionLabel}` : ' для действия в этом разделе'}.
      </Alert>
    </Snackbar>
    <CleanDialog open={open} onClose={dismiss} fullWidth maxWidth="sm">
      <CleanDialogTitle title="Запрос доступа" />
      <CleanDialogContent><Stack spacing={2} sx={{pt:1}}>
        <Typography>{current?.permissionLabel || 'Доступ к действию в разделе'}</Typography>
        <Typography variant="body2" color="text.secondary">Администратор рассмотрит запрос. До изменения прав действие останется недоступно.</Typography>
        <TextField label="Для какой работы нужен доступ" multiline minRows={3} value={reason} onChange={e=>setReason(e.target.value)} inputProps={{maxLength:1000}} disabled={busy} autoFocus />
        {error && <Alert severity="error">{error}</Alert>}
      </Stack></CleanDialogContent>
      <CleanDialogActions><Button onClick={dismiss} disabled={busy}>Отмена</Button><Button variant="contained" onClick={send} disabled={busy || reason.trim().length<5}>{busy?'Отправка…':'Отправить запрос'}</Button></CleanDialogActions>
    </CleanDialog>
    <Snackbar open={!!success} autoHideDuration={9000} onClose={()=>setSuccess('')}><Alert severity="success" onClose={()=>setSuccess('')}>{success}</Alert></Snackbar>
  </>;
}
